import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import NewConversationDialog from "@/components/tpr/NewConversationDialog/NewConversationDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BreadcrumbItem, BreadcrumbLink } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { useMember } from "@/contexts/memberContext";
import { log } from "@/core/logger";
import { useMessages } from "@/hooks/useMessages";
import { getInitials } from "@/lib/utils";
import { MemberService } from "@/services/MemberService";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, ArrowLeft, Loader2, MessageSquarePlus, Send, Users } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { Conversation } from "@/types/Message";
import type { MessageableEntity } from "@/types/MessageableEntity";
import { useLocation } from "react-router";

type QuickMember = {
  memberId: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  role?: string[];
  practiceManagerLocations?: Array<{ id: string; name: string }>;
};

const MessagesView = () => {
  const { member } = useMember();
  const location = useLocation();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [memberCache, setMemberCache] = useState<Record<string, QuickMember>>({});
  const [messageInput, setMessageInput] = useState("");
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);
  const [isMembersDialogOpen, setIsMembersDialogOpen] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState<Conversation | null>(null);
  const [groupMembersDetails, setGroupMembersDetails] = useState<QuickMember[]>([]);
  const [isLoadingGroupMembers, setIsLoadingGroupMembers] = useState(false);
  const [initialRecipient, setInitialRecipient] = useState<MessageableEntity | null>(null);
  const { setBreadcrumb } = useBreadcrumb();

  const { conversations, messages, isLoading, error, loadConversationMessages, markConversationRead, sendMessage, loadConversations } = useMessages(
    member?.id || "",
  );

  // Refs for auto-scrolling message containers
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mobileMessagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change, when typing, or when conversation is selected
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    mobileMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, selectedConversationId]);

  // Deep-link handler: `/messages?conversation=<id>` pre-selects the conversation. Used by
  // the "request access to a location" flow which lands the requester here after the server
  // creates the conversation. Runs once after conversations have loaded; if the id isn't in
  // the list yet (very fresh group), we let the next conversations refresh re-trigger it via
  // the dependency on `conversations`.
  const hasProcessedConversationDeepLink = useRef(false);

  useEffect(() => {
    if (hasProcessedConversationDeepLink.current) return;
    if (!conversations.length) return;
    const params = new URLSearchParams(location.search);
    const targetId = params.get("conversation");
    if (!targetId) return;

    const match = conversations.find((c) => c.id === targetId);
    if (!match) return;

    hasProcessedConversationDeepLink.current = true;
    setSelectedConversationId(match.id);
    loadConversationMessages(match.id);

    // Drop the query string so a refresh doesn't keep re-applying the deep-link, but keep
    // the path. `replaceState` avoids creating an extra history entry.
    const url = new URL(window.location.href);
    url.searchParams.delete("conversation");
    window.history.replaceState({}, document.title, url.pathname + url.hash);
  }, [conversations, location.search, loadConversationMessages]);

  // Check if we have an initial recipient from navigation state
  const hasProcessedNavigationState = useRef(false);

  useEffect(() => {
    if (location.state?.recipient && member?.id && conversations.length >= 0 && !hasProcessedNavigationState.current) {
      const recipient = location.state.recipient as MessageableEntity;

      // Mark as processed to prevent re-running
      hasProcessedNavigationState.current = true;

      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);

      // Only handle member-to-member conversations (not groups)
      if (recipient.type === "member") {
        // Check if a conversation already exists with this member
        const existingConversation = conversations.find((conv) => {
          // Only check direct (non-group) conversations
          if (conv.isGroup) return false;

          // Check if the other participant is the recipient
          const otherParticipantId = conv.participants.find((id) => id !== member.id);
          return otherParticipantId === recipient.id;
        });

        if (existingConversation) {
          // Navigate to the existing conversation
          setSelectedConversationId(existingConversation.id);
          loadConversationMessages(existingConversation.id);
        } else {
          // No existing conversation, open new conversation dialog with recipient pre-selected
          setInitialRecipient(recipient);
          setIsNewConversationOpen(true);
        }
      } else {
        // For groups, always open new conversation dialog
        setInitialRecipient(recipient);
        setIsNewConversationOpen(true);
      }
    }
  }, [location.state, member?.id, conversations, loadConversationMessages]);

  useEffect(() => {
    setBreadcrumb(
      <>
        <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
          <BreadcrumbLink href="#">Messages</BreadcrumbLink>
        </BreadcrumbItem>
      </>,
    );
    return () => setBreadcrumb(null);
  }, [setBreadcrumb]);

  // Fetch member details for all conversation participants
  useEffect(() => {
    const fetchMembers = async () => {
      if (!conversations.length) return;

      const memberService = new MemberService();
      const memberIds = new Set<string>();

      conversations.forEach((conv) => {
        conv.participants.forEach((participantId) => {
          if (participantId !== member?.id && !memberCache[participantId]) {
            memberIds.add(participantId);
          }
        });
      });

      const fetchedMembers: Record<string, QuickMember> = { ...memberCache };
      await Promise.all(
        Array.from(memberIds).map(async (memberId) => {
          const fetchedMember = await memberService.quickMemberById(memberId);
          if (fetchedMember) {
            fetchedMembers[memberId] = fetchedMember;
          }
        }),
      );

      setMemberCache(fetchedMembers);
    };

    fetchMembers();
  }, [conversations, member?.id]);

  // Mark the whole conversation as read whenever it's open and still has unread messages.
  // Covers the initial open AND any message that arrives while it's on screen. Reading the
  // conversation (not individual messages) also clears group/location-request conversations,
  // whose recipients live in `recipientIds`. markConversationRead optimistically zeroes the
  // count, so this effect settles on the next render without looping.
  useEffect(() => {
    if (!member?.id || !selectedConversationId) return;
    const conv = conversations.find((c) => c.id === selectedConversationId);
    if (conv && (conv.unreadCount?.[member.id] || 0) > 0) {
      markConversationRead(selectedConversationId);
    }
  }, [conversations, selectedConversationId, member?.id, markConversationRead]);

  // Get the other participant in a conversation
  const getOtherParticipant = (conversation: Conversation): QuickMember | null => {
    const otherParticipantId = conversation.participants.find((id) => id !== member?.id);
    return otherParticipantId ? memberCache[otherParticipantId] || null : null;
  };

  const getConversationDisplayInfo = (conversation: Conversation) => {
    if (conversation.isGroup) {
      // Debug logging to check avatar data
      console.log("[MessagesView] Group conversation:", {
        id: conversation.id,
        name: conversation.groupMetadata?.name,
        avatarUrl: conversation.groupMetadata?.avatarUrl,
        hasAvatarUrl: !!conversation.groupMetadata?.avatarUrl,
        groupMetadata: conversation.groupMetadata,
      });

      return {
        type: "group" as const,
        name: conversation.groupMetadata?.name || "Group",
        avatar: conversation.groupMetadata?.avatarUrl || null,
        subtitle: `${conversation.groupMetadata?.memberCount || 0} members`,
        initials: (conversation.groupMetadata?.memberCount || 0).toString(),
        pmLocations: [] as Array<{ id: string; name: string }>,
      };
    } else {
      const otherParticipantId = conversation.participants.find((id) => id !== member?.id);
      const otherMember = otherParticipantId ? memberCache[otherParticipantId] : null;

      if (!otherMember) {
        return {
          type: "member" as const,
          name: "Unknown User",
          avatar: null,
          subtitle: "",
          initials: "?",
          pmLocations: [] as Array<{ id: string; name: string }>,
        };
      }

      return {
        type: "member" as const,
        name: `${otherMember.firstName} ${otherMember.lastName}`,
        avatar: otherMember.avatar,
        subtitle: otherMember.email || "",
        initials: getInitials(`${otherMember.firstName} ${otherMember.lastName}`),
        pmLocations: otherMember.role?.includes("practice_manager") ? (otherMember.practiceManagerLocations || []) : [],
      };
    }
  };

  const handleConversationClick = async (conversationId: string) => {
    setSelectedConversationId(conversationId);
    await loadConversationMessages(conversationId);
  };

  const handleBackToList = () => {
    setSelectedConversationId(null);
  };

  const handleNewConversation = () => {
    setIsNewConversationOpen(true);
  };

  const handleNewConversationSend = async (params: {
    type: "member" | "group";
    recipientId?: string;
    locationIds?: string[];
    content: string;
  }) => {
    if (!member?.id) return;

    await sendMessage(params);

    // Generate the conversation ID
    let conversationId: string;
    if (params.type === "group" && params.locationIds) {
      // For groups, use the group conversation ID format
      const sortedLocationIds = [...params.locationIds].sort();
      conversationId =
        sortedLocationIds.length === 1
          ? `group:location:${sortedLocationIds[0]}`
          : `group:locations:${sortedLocationIds.join(",")}`;
    } else if (params.type === "member" && params.recipientId) {
      // For one-to-one, use the member conversation ID format
      conversationId = [member.id, params.recipientId].sort().join("_");
    } else {
      return; // Invalid params
    }

    // The broadcast will reload conversations automatically, but we need to select and load the new conversation
    // Wait a brief moment for the broadcast to complete
    setTimeout(async () => {
      setSelectedConversationId(conversationId);
      await loadConversationMessages(conversationId);
    }, 100);
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversationId) return;

    try {
      const currentConversation = conversations.find((c) => c.id === selectedConversationId);
      if (!currentConversation) return;

      if (currentConversation.isGroup) {
        if (currentConversation.groupType === "custom") {
          // Custom group - send to existing conversation by ID
          await sendMessage({
            type: "conversation",
            conversationId: currentConversation.id,
            content: messageInput,
          });
        } else if (currentConversation.groupMetadata?.locationIds) {
          // Location-based group - use locationIds
          await sendMessage({
            type: "group",
            locationIds: currentConversation.groupMetadata.locationIds,
            content: messageInput,
          });
        }
      } else if (currentOtherParticipant) {
        // Send to individual
        await sendMessage({
          type: "member",
          recipientId: currentOtherParticipant.memberId,
          content: messageInput,
        });
      }
      setMessageInput("");
      // Reload messages to show the sent message immediately
      await loadConversationMessages(selectedConversationId);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Unable to send message. Please try again.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter, new line on Shift+Enter
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleShowGroupMembers = async (conversation: Conversation) => {
    if (!conversation.isGroup) return;

    setSelectedGroupForMembers(conversation);
    setIsMembersDialogOpen(true);
    setIsLoadingGroupMembers(true);

    try {
      const memberService = new MemberService();
      const memberDetails = await Promise.all(
        conversation.participants.map(async (participantId) => {
          const fetchedMember = await memberService.quickMemberById(participantId);
          return fetchedMember;
        }),
      );

      setGroupMembersDetails(memberDetails.filter((m): m is QuickMember => m !== null));
    } catch (error) {
      console.error("Failed to fetch group members:", error);
      setGroupMembersDetails([]);
    } finally {
      setIsLoadingGroupMembers(false);
    }
  };

  const currentConversation = conversations.find((c) => c.id === selectedConversationId);
  const currentOtherParticipant = currentConversation ? getOtherParticipant(currentConversation) : null;

  const formatTimestamp = (date: string | Date) => {
    try {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch {
      return "";
    }
  };

  if (!member) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Please log in to view messages</p>
        </div>
      </div>
    );
  }

  if (error) {
    log("MessagesView", "Error loading messages:", error);
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Failed to load messages</p>
          <p className="text-sm text-gray-500 mb-4">{error}</p>
          <Button variant="outline" onClick={() => loadConversations()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const renderAuthorMessageBlock = () => {
    return (
      <>
        <div className="p-4 border-t border-gray-200 bg-white flex flex-row items-center gap-3">
          <Avatar className="w-10 h-10 flex-shrink-0">
            <AvatarImage
              src={
                member?.avatar ||
                "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
              }
              alt={member ? `${member.firstName} ${member.lastName}` : "User Avatar"}
            />
            <AvatarFallback>{member ? getInitials(`${member.firstName} ${member.lastName}`) : "?"}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <Textarea
              rows={3}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Say something..."
              className="w-full h-12 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <Button
              onClick={handleSendMessage}
              disabled={!messageInput.trim()}
              className="h-12 min-w-12 w-full bg-black hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Send message"
              data-testid="send-message-button"
            >
              <Send />
            </Button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="w-full h-full flex overflow-hidden">
      {/* Conversation List - Hidden on mobile when conversation is selected */}
      <div
        className={`
          w-full lg:w-1/3 lg:min-w-[320px] lg:max-w-[400px]
          ${selectedConversationId ? "hidden lg:flex" : "flex"}
          flex-col border-r border-gray-200
        `}
      >
        <div className="flex-1 overflow-y-auto">
          {isLoading && conversations.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex items-center justify-center h-32 px-4">
              <p className="text-gray-400 text-center">No conversations yet. Start a new one!</p>
            </div>
          ) : (
            conversations.map((conversation) => {
              const displayInfo = getConversationDisplayInfo(conversation);
              const hasUnread = conversation.unreadCount[member.id] > 0;

              return (
                <button
                  key={conversation.id}
                  onClick={() => handleConversationClick(conversation.id)}
                  className={`
                    w-full p-4 text-left border-b border-gray-100
                    hover:bg-gray-50 transition-colors
                    ${selectedConversationId === conversation.id ? "bg-blue-50" : ""}
                  `}
                >
                  <div className="flex gap-3">
                    {displayInfo.type === "group" ? (
                      displayInfo.avatar ? (
                        <Avatar className="w-10 h-10 flex-shrink-0">
                          <AvatarImage
                            src={displayInfo.avatar}
                            alt={displayInfo.name}
                            onError={(e) => {
                              console.error("[MessagesView] Avatar image failed to load:", {
                                src: displayInfo.avatar,
                                error: e,
                                groupName: displayInfo.name,
                              });
                            }}
                          />
                          <AvatarFallback className="bg-indigo-100 text-indigo-600">
                            {displayInfo.initials}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-indigo-600 font-semibold text-sm">{displayInfo.initials}</span>
                        </div>
                      )
                    ) : (
                      <Avatar className="w-10 h-10 flex-shrink-0">
                        <AvatarImage src={displayInfo.avatar || ""} alt={displayInfo.name} />
                        <AvatarFallback>{displayInfo.initials}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="body-font text-lg font-semibold text-gray-900">{displayInfo.name}</h3>
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(conversation.lastMessage.createdAt)}
                        </span>
                      </div>
                      {displayInfo.pmLocations.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-1">
                          {displayInfo.pmLocations.map((loc) => (
                            <span key={loc.id} className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded-full">
                              {loc.name}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col flex-1 min-w-0 mr-2">
                          <p className="text-sm text-gray-600 truncate">{conversation.lastMessage.content}</p>
                          {displayInfo.type === "group" && (
                            <span className="text-xs text-gray-500 mt-2">{displayInfo.subtitle}</span>
                          )}
                        </div>
                        {hasUnread && <span className="w-2 h-2 bg-indigo-600 rounded-full flex-shrink-0"></span>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* New Conversation Button - Fixed at bottom */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <Button
            onClick={handleNewConversation}
            className="w-full h-10 flex items-center justify-center gap-2 px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 hover:cursor-pointer transition-colors font-medium"
          >
            <MessageSquarePlus className="w-6 h-6" />
            New Conversation
          </Button>
        </div>
      </div>

      {/* Message Detail - Mobile: Slides in from right, Desktop: Always visible */}
      <div className="hidden lg:flex lg:flex-1 items-center justify-center bg-gray-50 relative">
        {!selectedConversationId ? (
          <p className="text-gray-400">Select a conversation to view messages</p>
        ) : (
          <div className="w-full h-full flex flex-col">
            {/* Message header */}
            <div className="p-4 border-b border-gray-200 bg-white flex items-center gap-3">
              {(() => {
                const currentConversation = conversations.find((c) => c.id === selectedConversationId);
                if (!currentConversation) return null;
                const displayInfo = getConversationDisplayInfo(currentConversation);

                return (
                  <>
                    {displayInfo.type === "group" ? (
                      displayInfo.avatar ? (
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={displayInfo.avatar} alt={displayInfo.name} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-600">
                            {displayInfo.initials}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-indigo-600 font-semibold text-xs">{displayInfo.initials}</span>
                        </div>
                      )
                    ) : (
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={displayInfo.avatar || ""} alt={displayInfo.name} />
                        <AvatarFallback>{displayInfo.initials}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className="flex flex-col">
                      <h2 className="body-font text-2xl font-semibold text-gray-900">{displayInfo.name}</h2>
                      {displayInfo.pmLocations.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {displayInfo.pmLocations.map((loc) => (
                            <span key={loc.id} className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded-full">
                              {loc.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {displayInfo.type === "group" && currentConversation && (
                        <button
                          onClick={() => handleShowGroupMembers(currentConversation)}
                          className="text-xs text-gray-500 hover:text-indigo-600 flex items-center gap-1 transition-colors"
                        >
                          <Users className="w-3 h-3" />
                          {displayInfo.subtitle}
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-4 relative">
              <div
                className="absolute z-0 inset-0 h-full w-full 
bg-[radial-gradient(circle,#73737350_1px,transparent_1px)] 
bg-[size:10px_10px] 
[mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_40%,transparent_100%)]"
              />
              <div className="absolute z-10 inset-0 p-4 lg:p-8">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-gray-400 text-center">No messages yet</p>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => {
                      const isSender = message.senderId === member?.id;
                      const currentConversation = conversations.find((c) => c.id === selectedConversationId);
                      const isGroupConversation = currentConversation?.isGroup || false;

                      // For group conversations, fetch the sender from cache
                      // For one-to-one, use the existing logic
                      const messageMember = isSender
                        ? member
                        : isGroupConversation
                          ? memberCache[message.senderId]
                          : currentOtherParticipant;

                      return (
                        <div key={message.id} className={`flex gap-2 ${isSender ? "justify-end" : "justify-start"}`}>
                          {!isSender && (
                            <Avatar className="w-8 h-8 flex-shrink-0">
                              <AvatarImage
                                src={
                                  messageMember?.avatar ||
                                  "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                                }
                                alt={
                                  messageMember ? `${messageMember.firstName} ${messageMember.lastName}` : "User Avatar"
                                }
                              />
                              <AvatarFallback>
                                {messageMember
                                  ? getInitials(`${messageMember.firstName} ${messageMember.lastName}`)
                                  : "?"}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <div
                            className={`max-w-[70%] shadow rounded-lg px-4 py-2 ${
                              isSender ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-900"
                            }`}
                          >
                            {isGroupConversation && !isSender && messageMember && (
                              <p
                                className={`text-xs font-semibold mb-1 ${isSender ? "text-blue-100" : "text-gray-700"}`}
                              >
                                {messageMember.firstName} {messageMember.lastName}
                              </p>
                            )}
                            <p className="text-sm">{message.content}</p>
                            <p className={`text-xs mt-1 ${isSender ? "text-blue-100" : "text-gray-500"}`}>
                              {formatTimestamp(message.createdAt)}
                            </p>
                          </div>
                          {isSender && (
                            <Avatar className="w-8 h-8 flex-shrink-0">
                              <AvatarImage
                                src={
                                  messageMember?.avatar ||
                                  "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                                }
                                alt={
                                  messageMember ? `${messageMember.firstName} ${messageMember.lastName}` : "User Avatar"
                                }
                              />
                              <AvatarFallback>
                                {messageMember
                                  ? getInitials(`${messageMember.firstName} ${messageMember.lastName}`)
                                  : "?"}
                              </AvatarFallback>
                            </Avatar>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
            </div>
            {/* Input area */}
            {renderAuthorMessageBlock()}
          </div>
        )}
      </div>

      {/* Mobile: Animated slide-in panel */}
      <AnimatePresence>
        {selectedConversationId && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed inset-0 z-50 bg-white flex flex-col lg:hidden"
          >
            {/* Header with back button */}
            <div className="p-4 border-b border-gray-200 bg-white flex items-center gap-3">
              <button
                onClick={handleBackToList}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Back to conversations"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              {(() => {
                const currentConversation = conversations.find((c) => c.id === selectedConversationId);
                if (!currentConversation) return null;
                const displayInfo = getConversationDisplayInfo(currentConversation);

                return (
                  <>
                    {displayInfo.type === "group" ? (
                      displayInfo.avatar ? (
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={displayInfo.avatar} alt={displayInfo.name} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-600">
                            {displayInfo.initials}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-indigo-600 font-semibold text-xs">{displayInfo.initials}</span>
                        </div>
                      )
                    ) : (
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={displayInfo.avatar || ""} alt={displayInfo.name} />
                        <AvatarFallback>{displayInfo.initials}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className="flex flex-col">
                      <h2 className="body-font text-2xl font-semibold text-gray-900">{displayInfo.name}</h2>
                      {displayInfo.pmLocations.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {displayInfo.pmLocations.map((loc) => (
                            <span key={loc.id} className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded-full">
                              {loc.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {displayInfo.type === "group" && currentConversation && (
                        <button
                          onClick={() => handleShowGroupMembers(currentConversation)}
                          className="text-xs text-gray-500 hover:text-indigo-600 flex items-center gap-1 transition-colors"
                        >
                          <Users className="w-3 h-3" />
                          {displayInfo.subtitle}
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Messages area */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 relative">
              <div
                className="absolute z-0 inset-0 h-full w-full 
bg-[radial-gradient(circle,#73737350_1px,transparent_1px)] 
bg-[size:10px_10px] 
[mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#000_40%,transparent_100%)]"
              />
              <div className="absolute z-10 inset-0 p-4 lg:p-8">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-gray-400 text-center">No messages yet</p>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => {
                      const isSender = message.senderId === member?.id;
                      const currentConversation = conversations.find((c) => c.id === selectedConversationId);
                      const isGroupConversation = currentConversation?.isGroup || false;

                      // For group conversations, fetch the sender from cache
                      // For one-to-one, use the existing logic
                      const messageMember = isSender
                        ? member
                        : isGroupConversation
                          ? memberCache[message.senderId]
                          : currentOtherParticipant;

                      return (
                        <div key={message.id} className={`flex gap-2 ${isSender ? "justify-end" : "justify-start"}`}>
                          {!isSender && (
                            <Avatar className="w-8 h-8 flex-shrink-0">
                              <AvatarImage
                                src={
                                  messageMember?.avatar ||
                                  "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                                }
                                alt={
                                  messageMember ? `${messageMember.firstName} ${messageMember.lastName}` : "User Avatar"
                                }
                              />
                              <AvatarFallback>
                                {messageMember
                                  ? getInitials(`${messageMember.firstName} ${messageMember.lastName}`)
                                  : "?"}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <div
                            className={`max-w-[70%] rounded-lg shadow px-4 py-2 ${
                              isSender ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-900"
                            }`}
                          >
                            {isGroupConversation && !isSender && messageMember && (
                              <p
                                className={`text-xs font-semibold mb-1 ${isSender ? "text-blue-100" : "text-gray-700"}`}
                              >
                                {messageMember.firstName} {messageMember.lastName}
                              </p>
                            )}
                            <p className="text-sm">{message.content}</p>
                            <p className={`text-xs mt-1 ${isSender ? "text-blue-100" : "text-gray-500"}`}>
                              {formatTimestamp(message.createdAt)}
                            </p>
                          </div>
                          {isSender && (
                            <Avatar className="w-8 h-8 flex-shrink-0">
                              <AvatarImage
                                src={
                                  messageMember?.avatar ||
                                  "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                                }
                                alt={
                                  messageMember ? `${messageMember.firstName} ${messageMember.lastName}` : "User Avatar"
                                }
                              />
                              <AvatarFallback>
                                {messageMember
                                  ? getInitials(`${messageMember.firstName} ${messageMember.lastName}`)
                                  : "?"}
                              </AvatarFallback>
                            </Avatar>
                          )}
                        </div>
                      );
                    })}
                    <div ref={mobileMessagesEndRef} />
                  </div>
                )}
              </div>
            </div>

            {/* Input area */}
            {renderAuthorMessageBlock()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Conversation Dialog */}
      <NewConversationDialog
        open={isNewConversationOpen}
        onOpenChange={(open) => {
          setIsNewConversationOpen(open);
          if (!open) {
            // Clear initial recipient when dialog closes
            setInitialRecipient(null);
          }
        }}
        onSendMessage={handleNewConversationSend}
        initialEntity={initialRecipient}
      />

      {/* Group Members Dialog */}
      <Dialog open={isMembersDialogOpen} onOpenChange={setIsMembersDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Users className="w-5 h-5 text-black" />
              {selectedGroupForMembers?.groupMetadata?.name || "Group"} Members
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {isLoadingGroupMembers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : groupMembersDetails.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No members found</div>
            ) : (
              <div className="border rounded-lg max-h-96 overflow-y-auto">
                <div className="divide-y">
                  {groupMembersDetails.map((groupMember) => (
                    <div key={groupMember.memberId} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                      <Avatar className="w-10 h-10">
                        <AvatarImage
                          src={groupMember.avatar}
                          alt={`${groupMember.firstName} ${groupMember.lastName}`}
                        />
                        <AvatarFallback className="bg-indigo-100 text-indigo-600">
                          {getInitials(`${groupMember.firstName} ${groupMember.lastName}`)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate" data-testid="group-member-name">
                          {groupMember.firstName} {groupMember.lastName}
                          {groupMember.memberId === member?.id && (
                            <span className="text-xs text-gray-500 ml-2">(You)</span>
                          )}
                        </p>
                        {/* Email intentionally hidden in the group members modal (2026-05-19) — avatar + name only. */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesView;
